import { useQuery } from '@tanstack/react-query';
import { fetchDMs } from '../lib/api';
import { friendlyPeerName } from '../lib/peerName';
import { Composer } from './Composer';
import { MessageThread } from './MessageThread';
import { Skeleton } from './Skeleton';

interface DMViewProps {
  dmId: string;
  /** Suppress DMView's own header block (the peer name + `@uri`). Set when DMView is
   *  embedded under a host header that already names the peer — e.g. the /org inspector's
   *  Chat tab, whose head already shows the agent name + live chip — so the chat area
   *  starts straight at the thread (and the `@uri` we cut from headers stays out). Default
   *  false: the standalone messaging console keeps its header unchanged. */
  hideHeader?: boolean;
  /** T-0198 — override the peer's display name in the folk copy (empty state + composer
   *  placeholder). The /org inspector passes the agent's canvas name ("Sage"), so those
   *  strings never fall back to the minted namespace `sprout-demo.sage` (the peer URI's
   *  local-part). Absent ⇒ the standalone console's derived name is used, unchanged. */
  peerName?: string;
}

export function DMView({ dmId, hideHeader = false, peerName }: DMViewProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['dms'],
    queryFn: fetchDMs,
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="dm-view dm-view-loading">
        <Skeleton
          className="dm-view-header-loading"
          ariaLabel="Loading direct message"
          rows={1}
          variant="text"
        />
      </div>
    );
  }

  const dm = data?.dms.find((d) => d.dm_id === dmId);
  // T-0198 — a caller-supplied folk name wins (the /org inspector knows the agent as
  // "Sage"); else derive from the DM peer, else the id. A blank override is ignored.
  const friendlyName =
    peerName?.trim() ||
    (dm ? friendlyPeerName(dm.peer_display_name, dm.peer_username) : undefined);
  const headerName = friendlyName ?? dmId;

  return (
    <div className="dm-view" aria-label={`Direct message with ${headerName}`}>
      {!hideHeader && (
        <header className="dm-view-header">
          <h2 className="dm-view-title" title={dm?.peer_username}>
            {headerName}
          </h2>
          {dm?.peer_username && dm.peer_username !== headerName && (
            <span className="dm-view-username">{dm.peer_username}</span>
          )}
        </header>
      )}
      <div className="dm-view-body">
        <MessageThread roomId={dmId} variant="dm" peerName={friendlyName} />
      </div>
      <Composer roomId={dmId} placeholder={`Message ${headerName}…`} variant="dm" />
    </div>
  );
}
